<template>
	<v-container fluid class="pa-3">
		<v-card class="rounded-xl" elevation="0">
			<v-card-title class="d-flex justify-space-between">
				<div>Product List</div>
				<v-text-field
					v-model="search"
					dense
					rounded
					outlined
					hide-details
					label="Search"
					clearable
					style="max-width: 300px"></v-text-field>
			</v-card-title>
			<v-card-text>
				<v-container
					v-if="filteredProducts.length == 0"
					class="d-flex justify-center">
					<div>
						<div class="text-h5 font-weight-bold">Nothing to see</div>
						<div class="text-center text-h5">
							<v-icon>mdi-spider</v-icon>
						</div>
					</div>
				</v-container>
				<div
					v-for="(row, rowIndex) in productRows"
					:key="`row-${rowIndex}`">
					<v-row>
						<v-col
							v-for="product in row"
							:key="product.id"
							cols="12"
							sm="6"
							md="4"
							lg="3">
							<v-card
								class="rounded-xl pa-2"
								elevation="1"
								outlined
								:class="{
									'primary': expandedProductId === product.id,
								}">
								<v-card-text class="pa-2">
									<div class="d-flex justify-center mb-2">
										<img
											class="product-image"
											loading="lazy"
											:src="getProductPictureUrlById(product.pictureId)"
											@error="handleImageError" />
									</div>
									<div
										class="text-subtitle-1 font-weight-bold text-truncate"
										:class="{
											'white--text': expandedProductId === product.id,
										}">
										{{ product.title }}
									</div>
									<div
										class="text-caption"
										:class="{
											'white--text': expandedProductId === product.id,
											'grey--text': expandedProductId !== product.id,
										}">
										Qty: {{ product.quantity }}
									</div>
								</v-card-text>
								<v-card-actions class="pa-0 pt-2">
									<v-btn
										text
										rounded
										block
										small
										:color="
											expandedProductId === product.id
												? 'white'
												: 'primary'
										"
										@click="toggleExpand(product.id)">
										<v-icon left>
											{{
												expandedProductId === product.id
													? "mdi-chevron-up"
													: "mdi-chevron-down"
											}}
										</v-icon>
										{{
											expandedProductId === product.id
												? "Show Less"
												: "Show More"
										}}
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-col>
					</v-row>
					<!-- Expanded panel appears after the row containing the expanded product -->
					<v-expand-transition>
						<v-card
							v-if="getExpandedProductInRow(row)"
							class="rounded-xl mb-4 mt-2"
							elevation="1"
							outlined>
							<v-card-title class="d-flex justify-space-between flex-wrap">
								<div class="text-h6">
									{{ getExpandedProductInRow(row).title }} - Details
								</div>
								<div class="d-flex justify-end flex-wrap" style="gap: 5px">
									<v-btn
										color="warning"
										depressed
										rounded
										small
										@click="
											$router.push({
												name: 'ProductUpdate',
												params: {
													id: getExpandedProductInRow(row).id,
												},
											})
										">
										<v-icon left small>mdi-pencil</v-icon>
										Update
									</v-btn>
									<v-btn
										color="error"
										depressed
										rounded
										small
										@click="deleteProduct(getExpandedProductInRow(row).id)">
										<v-icon left small>mdi-delete</v-icon>
										Delete
									</v-btn>
								</div>
							</v-card-title>
							<v-card-text>
								<v-simple-table>
									<thead>
										<tr>
											<th class="text-left">Id</th>
											<th class="text-left">Title</th>
											<th class="text-left">Quantity</th>
											<th class="text-left">Active</th>
											<th class="text-left">Date Created</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td>{{ getExpandedProductInRow(row).id }}</td>
											<td>{{ getExpandedProductInRow(row).title }}</td>
											<td>{{ getExpandedProductInRow(row).quantity }}</td>
											<td>
												<v-chip
													small
													:color="
														getExpandedProductInRow(row).isActive
															? 'success'
															: 'error'
													">
													{{
														getExpandedProductInRow(row).isActive
															? "Yes"
															: "No"
													}}
												</v-chip>
											</td>
											<td>{{ getExpandedProductInRow(row).dateCreated }}</td>
										</tr>
									</tbody>
								</v-simple-table>
							</v-card-text>
						</v-card>
					</v-expand-transition>
				</div>
			</v-card-text>
		</v-card>
	</v-container>
</template>
<script>
import backendService from "@/services/backend-service.js";
export default {
	name: "ProductList",
	data() {
		return {
			products: [],
			search: "",
			expandedProductId: null,
		};
	},
	mounted: function () {
		this.getProducts();
	},
	computed: {
		filteredProducts() {
			let products = this.products;
			let searchStr = this.search;
			searchStr = searchStr ? searchStr.trim().toLowerCase() : "";
			if (searchStr && searchStr.length) {
				products = products.filter((e) => {
					let str = e.title || "";
					return str
						.toString()
						.trim()
						.toLowerCase()
						.includes(searchStr.toLowerCase());
				});
			}
			return products;
		},
		columnsPerRow() {
			if (this.$vuetify.breakpoint.lgAndUp) return 4;
			if (this.$vuetify.breakpoint.md) return 3;
			if (this.$vuetify.breakpoint.sm) return 2;
			return 1;
		},
		productRows() {
			const rows = [];
			const cols = this.columnsPerRow;
			for (let i = 0; i < this.filteredProducts.length; i += cols) {
				rows.push(this.filteredProducts.slice(i, i + cols));
			}
			return rows;
		},
	},
	methods: {
		toggleExpand(productId) {
			if (this.expandedProductId === productId) {
				this.expandedProductId = null;
			} else {
				this.expandedProductId = productId;
			}
		},
		getExpandedProductInRow(row) {
			return row.find((p) => p.id === this.expandedProductId);
		},
		getProductPictureUrlById(imageId) {
			return backendService.getProductPictureUrlById(imageId);
		},
		handleImageError(e) {
			e.target.src = "";
		},
		getProducts() {
			backendService.getProductsByStoreId(1).then((response) => {
				this.products = response.data;
			});
		},
		deleteProduct(productId) {
			backendService.deleteProductById(productId).then(() => {
				this.products = this.products.filter((x) => x.id != productId);
				this.expandedProductId = null;
			});
		},
		convertToCleanNumber(raw) {
			return raw.toLocaleString("en-US", {
				style: "currency",
				currency: "USD",
			});
		},
	},
};
</script>
<style scoped>
.product-image {
	object-fit: contain;
	image-rendering: smooth;
	width: 100px;
	height: 100px;
	border-radius: 8px;
}
</style>
